import { useEffect, useState } from 'react';
import AuthUser from './AuthUser';
import toast, { Toaster } from "react-hot-toast";
import moment from 'moment';
import TaskSkeleton from './TaskSkeleton';
import * as XLSX from 'xlsx';

export default function AntivirusMasterList() {
    const { http, user } = AuthUser();
    const [antiviruses, setAntiviruses] = useState([]);
    const [typeNamesList, setTypeNamesList] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [editingAntivirus, setEditingAntivirus] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('available'); // Used = No
    const [filterTypeName, setFilterTypeName] = useState(''); // Filter by type name
    const [filterTypeYear, setFilterTypeYear] = useState(''); // Filter by type year
    const [filterInvoiced, setFilterInvoiced] = useState('No'); // Invoice = No
    const [filterRequestedStaff, setFilterRequestedStaff] = useState(''); // Filter by requested staff name
    const [formData, setFormData] = useState({
        type_name: '',
        type_year: '',
        key: '',
        invoiced: '',
        remarks: '',
        who_used: ''
    });

    // Super Admin only (same as Company menu / antivirus admin checks)
    const isSuperAdmin = user && (user.id == 1 || user.id == 3);

    const getRequestedStaffName = (antivirus) => {
        const staff = antivirus.antivirus_request?.created_by_user;
        if (!staff) return '';
        return [staff.name, staff.last_name].filter(Boolean).join(' ').trim();
    };

    const requestedStaffOptions = [...new Set(
        antiviruses.map(getRequestedStaffName).filter(Boolean)
    )].sort((a, b) => a.localeCompare(b));

    const matchesFilters = (antivirus) => {
        const searchLower = searchTerm.toLowerCase();

        const matchesSearch = searchTerm === '' ||
            (antivirus.key || '').toLowerCase().includes(searchLower) ||
            (antivirus.type_name || '').toLowerCase().includes(searchLower) ||
            (antivirus.type_year || '').toLowerCase().includes(searchLower);

        const matchesTypeName = filterTypeName === '' ||
            antivirus.type_name === filterTypeName;

        const matchesTypeYear = filterTypeYear === '' ||
            antivirus.type_year === filterTypeYear;

        const matchesStatus = filterStatus === '' ||
            (filterStatus === 'used' && antivirus.is_used === 1) ||
            (filterStatus === 'available' && antivirus.is_used === 0);

        const matchesInvoiced = filterInvoiced === '' ||
            (filterInvoiced === 'Yes' && antivirus.invoiced === 'Yes') ||
            (filterInvoiced === 'No' && (antivirus.invoiced === 'No' || !antivirus.invoiced));

        const matchesStaff = filterRequestedStaff === '' ||
            getRequestedStaffName(antivirus) === filterRequestedStaff;

        return matchesSearch && matchesTypeName && matchesTypeYear && matchesStatus && matchesInvoiced && matchesStaff;
    };

    const handleExcel = () => {
        const filtered = antiviruses.filter(matchesFilters);
        const rows = filtered.map((av) => ({
            ID: av.id,
            "Type Name": av.type_name || "",
            "Type Year": av.type_year || "",
            Key: av.key || "",
            "Is Used": av.is_used === 1 ? "Yes" : "No",
            Invoiced: av.invoiced || "",
            Remarks: av.remarks || "",
            "Who Used": av.who_used || "",
            "Used On": av.used_on ? moment(av.used_on).format("DD/MM/YYYY") : "",
            "Created At": av.created_at ? moment(av.created_at).format("DD/MM/YYYY HH:mm") : "",
            "Company Name": av.antivirus_request?.company_name || "",
            "Requested Staff": getRequestedStaffName(av),
        }));
        const ws = XLSX.utils.json_to_sheet(rows);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Antivirus Master");
        XLSX.writeFile(wb, `antivirus_master_${moment().format("YYYYMMDD_HHmm")}.xlsx`);
    };

    useEffect(() => {
        fetchAntiviruses();
        fetchTypeNames();
    }, []);

    const fetchAntiviruses = () => {
        setIsLoading(true);
        http.get('/antivirus_master').then((res) => {
            setIsLoading(false);
            if (res.data.success && Array.isArray(res.data.data)) {
                setAntiviruses(res.data.data);
            } else {
                console.error("API response error:", res.data);
                toast.error("Failed to fetch antivirus entries");
            }
        }).catch(error => {
            setIsLoading(false);
            console.error("Error fetching antivirus entries:", error);
            toast.error("Error fetching antivirus entries");
        });
    };

    const fetchTypeNames = async () => {
        try {
            const response = await http.get('/type_name_antivirus_master');
            if (response.data.success) {
                setTypeNamesList(response.data.data);
            }
        } catch (error) {
            console.error("Error fetching type names:", error);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);

        // Add updated_by with current user's ID
        const submitData = {
            ...formData,
            updated_by: user.id
        };

        try {
            let response;
            if (editingAntivirus) {
                if (!isSuperAdmin) {
                    toast.error("Only Super Admin can edit");
                    setIsLoading(false);
                    return;
                }
                response = await http.post(`/antivirus_master/${editingAntivirus.id}`, {
                    _method: 'PUT',
                    ...submitData
                });
                toast.success("Antivirus entry updated successfully!");
            } else {
                response = await http.post('/antivirus_master', submitData);
                toast.success("Antivirus entry created successfully!");
            }

            if (response.data.success) {
                setShowModal(false);
                setFormData({
                    type_name: '',
                    type_year: '',
                    key: '',
                    invoiced: '',
                    remarks: '',
                    who_used: ''
                });
                setEditingAntivirus(null);
                fetchAntiviruses();
            }
        } catch (error) {
            console.error("Error saving antivirus entry:", error);
            const errorMessage = error.response?.data?.message || "Error saving antivirus entry";
            toast.error(errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    const handleEdit = (antivirus) => {
        if (!isSuperAdmin) return;
        setEditingAntivirus(antivirus);
        setFormData({
            type_name: antivirus.type_name || '',
            type_year: antivirus.type_year || '',
            key: antivirus.key || '',
            invoiced: antivirus.invoiced || '',
            remarks: antivirus.remarks || '',
            who_used: antivirus.who_used || ''
        });
        setShowModal(true);
    };

    const handleDelete = async (id) => {
        if (window.confirm("Are you sure you want to delete this antivirus entry?")) {
            try {
                const response = await http.delete(`/antivirus_master/${id}`);
                if (response.data.success) {
                    toast.success("Antivirus entry deleted successfully!");
                    fetchAntiviruses();
                }
            } catch (error) {
                console.error("Error deleting antivirus entry:", error);
                toast.error("Error deleting antivirus entry");
            }
        }
    };

    const resetForm = () => {
        setFormData({
            type_name: '',
            type_year: '',
            key: '',
            invoiced: '',
            remarks: '',
            who_used: ''
        });
        setEditingAntivirus(null);
        setShowModal(false);
    };

    return (
        <div className="container-fluid">
            <Toaster />
            <div className="row">
                <div className="col-12">
                    <div className="card">
                        <div className="card-header d-flex justify-content-between align-items-center">
                            <h3 className="card-title mb-0">Antivirus Master</h3>
                            <div className="d-flex gap-2">
                                <button
                                    className="btn btn-sm btn-outline-success"
                                    disabled={antiviruses.length === 0}
                                    onClick={handleExcel}
                                >
                                    ⬇ Excel
                                </button>
                                <button 
                                    className="btn btn-sm btn-primary"
                                    onClick={() => setShowModal(true)}
                                >
                                    <i className="fas fa-plus-circle me-1"></i> Add Entry
                                </button>
                            </div>
                        </div>
                        <div className="card-body">
                            {/* Filter Section */}
                            <div className="row mb-3 g-2">
                                <div className="col-md-2">
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Search by key..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                </div>
                                <div className="col-md-2">
                                    <select
                                        className="form-select"
                                        value={filterTypeName}
                                        onChange={(e) => setFilterTypeName(e.target.value)}
                                    >
                                        <option value="">All Type Names</option>
                                        {[...new Set(antiviruses.map(av => av.type_name))].filter(Boolean).map((name, idx) => (
                                            <option key={idx} value={name}>{name}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="col-md-2">
                                    <select
                                        className="form-select"
                                        value={filterTypeYear}
                                        onChange={(e) => setFilterTypeYear(e.target.value)}
                                    >
                                        <option value="">All Type Years</option>
                                        <option value="1 year">1 year</option>
                                        <option value="2 year">2 year</option>
                                        <option value="3 year">3 year</option>
                                    </select>
                                </div>
                                <div className="col-md-2">
                                    <select
                                        className="form-select"
                                        value={filterStatus}
                                        onChange={(e) => setFilterStatus(e.target.value)}
                                    >
                                        <option value="">All Status</option>
                                        <option value="used">Used</option>
                                        <option value="available">Available</option>
                                    </select>
                                </div>
                                <div className="col-md-2">
                                    <select
                                        className="form-select"
                                        value={filterInvoiced}
                                        onChange={(e) => setFilterInvoiced(e.target.value)}
                                    >
                                        <option value="">All Invoice</option>
                                        <option value="Yes">Invoice Yes</option>
                                        <option value="No">Invoice No</option>
                                    </select>
                                </div>
                                <div className="col-md-2">
                                    <select
                                        className="form-select"
                                        value={filterRequestedStaff}
                                        onChange={(e) => setFilterRequestedStaff(e.target.value)}
                                    >
                                        <option value="">All Requested Staff</option>
                                        {requestedStaffOptions.map((name) => (
                                            <option key={name} value={name}>{name}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            {isLoading ? (
                                <TaskSkeleton />
                            ) : (
                                <div className="table-responsive">
                                    <table className="table table-bordered table-striped">
                                        <thead>
                                            <tr>
                                                <th>ID</th>
                                                <th>Type Name</th>
                                                <th>Type Year</th>
                                                <th>Key</th>
                                                <th>Is Used</th>
                                                <th>Invoiced</th>
                                                <th>Remarks</th>
                                                <th>Who Used</th>
                                                <th>Used On</th>
                                                <th>Created At</th>
                                                <th>Company Name</th>
                                                <th>Requested Staff</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {antiviruses.filter(matchesFilters).length > 0 ? (
                                                antiviruses.filter(matchesFilters).map((antivirus) => (
                                                    <tr key={antivirus.id}>
                                                        <td>{antivirus.id}</td>
                                                        <td>{antivirus.type_name || '-'}</td>
                                                        <td>{antivirus.type_year || '-'}</td>
                                                        <td>{antivirus.key || '-'}</td>
                                                        <td>
                                                            <span 
                                                                className={`badge ${antivirus.is_used === 1 ? 'bg-success' : 'bg-danger'}`}
                                                                style={{ fontSize: '12px' }}
                                                            >
                                                                {antivirus.is_used === 1 ? 'Yes' : 'No'}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            {antivirus.invoiced ? (
                                                                <span className={`badge ${antivirus.invoiced === 'Yes' ? 'bg-success' : 'bg-secondary'}`} style={{ fontSize: '12px' }}>
                                                                    {antivirus.invoiced}
                                                                </span>
                                                            ) : '-'}
                                                        </td>
                                                        <td style={{ maxWidth: '180px' }} title={antivirus.remarks || ''}>
                                                            {(antivirus.remarks || '-').slice(0, 50)}{(antivirus.remarks || '').length > 50 ? '…' : ''}
                                                        </td>
                                                        <td style={{ maxWidth: '120px' }} title={antivirus.who_used || ''}>
                                                            {(antivirus.who_used || '-').slice(0, 30)}{(antivirus.who_used || '').length > 30 ? '…' : ''}
                                                        </td>
                                                        <td>{antivirus.used_on ? moment(antivirus.used_on).format('DD/MM/YYYY') : '-'}</td>
                                                        <td>{moment(antivirus.created_at).format('DD/MM/YYYY HH:mm')}</td>
                                                        <td
                                                            className="company-cell"
                                                            title={antivirus.antivirus_request?.company_name || ""}
                                                        >
                                                            {antivirus.antivirus_request?.company_name
                                                                ? antivirus.antivirus_request.company_name
                                                                : <span className="text-muted">-</span>}
                                                        </td>
                                                        <td>
                                                            {antivirus.antivirus_request?.created_by_user
                                                                ? <span className="badge bg-info text-dark" style={{ fontSize: '11px' }}>
                                                                    {[antivirus.antivirus_request.created_by_user.name, antivirus.antivirus_request.created_by_user.last_name].filter(Boolean).join(' ')}
                                                                  </span>
                                                                : <span className="text-muted">-</span>}
                                                        </td>
                                                        <td style={{ whiteSpace: 'nowrap' }}>
                                                            {isSuperAdmin && (
                                                                <button 
                                                                    className="btn btn-sm btn-warning me-1"
                                                                    onClick={() => handleEdit(antivirus)}
                                                                    style={{ fontSize: '11px', padding: '5px 10px' }}
                                                                    title="Edit"
                                                                >
                                                                    <i className="fas fa-pen"></i>
                                                                </button>
                                                            )}
                                                            {isSuperAdmin && (
                                                                <button 
                                                                    className="btn btn-sm btn-danger"
                                                                    onClick={() => handleDelete(antivirus.id)}
                                                                    style={{ fontSize: '11px', padding: '5px 10px' }}
                                                                    title="Delete"
                                                                >
                                                                    <i className="fas fa-trash-alt"></i>
                                                                </button>
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan="13" className="text-center">No antivirus entries found</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Modal */}
            {showModal && (
                <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-lg">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">
                                    {editingAntivirus ? 'Edit Antivirus Entry' : 'Add New Antivirus Entry'}
                                </h5>
                                <button 
                                    type="button" 
                                    className="btn-close" 
                                    onClick={resetForm}
                                ></button>
                            </div>
                            <form onSubmit={handleSubmit}>
                                <div className="modal-body">
                                    <div className="row">
                                        <div className="col-md-6">
                                            <div className="mb-3">
                                                <label className="form-label">Type Name</label>
                                                <select
                                                    className="form-select"
                                                    name="type_name"
                                                    value={formData.type_name}
                                                    onChange={handleInputChange}
                                                >
                                                    <option value="">Select Type Name</option>
                                                    {typeNamesList.map((typeName) => (
                                                        <option key={typeName.id} value={typeName.name}>
                                                            {typeName.name}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <div className="mb-3">
                                                <label className="form-label">Type Year</label>
                                                <select
                                                    className="form-select"
                                                    name="type_year"
                                                    value={formData.type_year}
                                                    onChange={handleInputChange}
                                                >
                                                    <option value="">Select Type Year</option>
                                                    <option value="1 year">1 year</option>
                                                    <option value="2 year">2 year</option>
                                                    <option value="3 year">3 year</option>
                                                </select>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="row">
                                        <div className="col-md-12">
                                            <div className="mb-3">
                                                <label className="form-label">Key</label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    name="key"
                                                    value={formData.key}
                                                    onChange={handleInputChange}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="row">
                                        <div className="col-md-4">
                                            <div className="mb-3">
                                                <label className="form-label">Invoiced</label>
                                                <select
                                                    className="form-select"
                                                    name="invoiced"
                                                    value={formData.invoiced}
                                                    onChange={handleInputChange}
                                                >
                                                    <option value="">--</option>
                                                    <option value="Yes">Yes</option>
                                                    <option value="No">No</option>
                                                </select>
                                            </div>
                                        </div>
                                        <div className="col-md-8">
                                            <div className="mb-3">
                                                <label className="form-label">Who Used</label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    name="who_used"
                                                    value={formData.who_used}
                                                    onChange={handleInputChange}
                                                    placeholder="Optional"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="row">
                                        <div className="col-md-12">
                                            <div className="mb-3">
                                                <label className="form-label">Remarks</label>
                                                <textarea
                                                    className="form-control"
                                                    name="remarks"
                                                    value={formData.remarks}
                                                    onChange={handleInputChange}
                                                    rows={3}
                                                    placeholder="Optional"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button 
                                        type="button" 
                                        className="btn btn-secondary" 
                                        onClick={resetForm}
                                    >
                                        Cancel
                                    </button>
                                    <button 
                                        type="submit" 
                                        className="btn btn-primary"
                                        disabled={isLoading}
                                    >
                                        {isLoading ? 'Saving...' : (editingAntivirus ? 'Update' : 'Create')}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
            <style>{`
                .company-cell {
                    max-width: 160px;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    vertical-align: middle;
                }
            `}</style>
        </div>
    );
}

